/**
 * WEB-1114 — requests whose 401 must never trigger a silent-refresh attempt.
 *
 * Split out from client.ts on purpose (see formDataContentType.ts's
 * docstring for the identical reasoning): client.ts reads `import.meta.env`
 * at module scope, which ts-jest cannot import directly, so this seam has
 * to live in its own framework-free module to be unit-testable.
 */
const REFRESH_EXEMPT_PREFIXES = [
  // Credential-verification endpoints: a 401 here means "wrong credentials"
  // (or an expired/invalid 2FA challenge token), never "expired session" —
  // there may not even be a session yet, so a refresh attempt makes no sense.
  '/auth/login',
  '/auth/register',
  '/auth/google',
  '/auth/2fa/verify',
  '/auth/2fa/send-code-challenge',
  // WEB-1349 подтема 1 п.5: same credential-verification reasoning as
  // above — a 401 here is "current password is incorrect" / Google reauth
  // mismatch (auth.service.ts changePassword / addPasswordToGoogleUser),
  // never "your access token expired".
  '/auth/change-password',
  '/auth/add-password',
  // The refresh/logout calls themselves — retrying a refresh via a refresh
  // would recurse for no benefit, and a failing logout should just fail.
  '/auth/refresh',
  '/auth/logout',
] as const;

export function isRefreshExemptUrl(url: string | undefined): boolean {
  if (!url) {
    return true;
  }
  return REFRESH_EXEMPT_PREFIXES.some((prefix) => url.startsWith(prefix));
}

/**
 * WEB-3970 кабинет — коды отказа 401, при которых пропуск НЕ обновляется.
 *
 * Передача и удаление организации требуют повторного подтверждения паролем или
 * через Google. Неверное подтверждение сервер отвечает 401 с одним из этих кодов —
 * это «не подошло», а не «сессия истекла». Обновить пропуск и повторить такой
 * запрос — значит отправить то же неверное подтверждение второй раз, и сервер
 * засчитает одну попытку дважды (пауза наступила бы после третьего нажатия, а не
 * после пятого). Перечень закрытый: 401 без кода и с любым другим кодом — прежнее
 * обновление пропуска и один повтор. Единственное место перечня — здесь; читает
 * его `sessionRefresh.ts` (`handleUnauthorized`).
 */
const REFRESH_EXEMPT_ERROR_CODES: ReadonlySet<string> = new Set([
  'PASSWORD_REQUIRED',
  'ORG_ACTION_PASSWORD_INVALID',
  'ORG_ACTION_GOOGLE_CONFIRM_REQUIRED',
  'ORG_ACTION_GOOGLE_CONFIRM_MISMATCH',
]);

export function isRefreshExemptErrorCode(code: unknown): boolean {
  return typeof code === 'string' && REFRESH_EXEMPT_ERROR_CODES.has(code);
}
